import React, { useEffect, useState } from "react";
import { useToast } from "../../context/ToastContext";
import { apiClient } from "../../utils/http";
import { EVENT_TYPE_META } from "../../utils/constants";
import { assignmentLabel, formatDate, formatTime12Hr } from "../../utils/format";
import { openWhatsApp, reminderText } from "../../utils/whatsapp";

const weekday = (iso) => {
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("en-US", { weekday: "long" });
};

/* Everything scheduled on one day, with the actions for each item. Sits above the calendar. */
export default function DateDetailsModal({ date, schedule, perms, onViewJob, onEditJob }) {
  const showToast = useToast();
  const { version, closeDay, openEventForm, toggleEvent, deleteEvent } = schedule;
  const [items, setItems] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get("schedule", { params: { from: date, to: date } })
      .then((res) => !cancelled && setItems(res.data.items))
      .catch((err) => !cancelled && showToast(err.message, "error"));
    return () => {
      cancelled = true;
    };
  }, [date, version, showToast]);

  const sendWhatsApp = (item) => {
    openWhatsApp(reminderText(item));
    showToast("Opening WhatsApp with reminder alert...", "info");
  };

  return (
    <div className="modal-backdrop no-print" style={{ zIndex: 1004 }}>
      <div className="modal-container modal-md">
        <div className="modal-header">
          <div className="wizard-title-group">
            <h3>
              Schedule: {weekday(date)}, {formatDate(date)}
            </h3>
          </div>
          <div className="header-action-group" style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <button type="button" className="btn-icon-close" title="Close" onClick={closeDay}>
              &times;
            </button>
          </div>
        </div>
        <div className="modal-body">
          <div className="schedule-items-list">
            {items === null ? (
              <div className="app-loader" style={{ minHeight: 160 }}>
                <div className="spinner" />
              </div>
            ) : items.length === 0 ? (
              <div className="empty-schedule-msg" style={{ textAlign: "center", padding: "2.5rem 1rem" }}>
                <p style={{ fontSize: "0.95rem", color: "var(--text-muted)", marginBottom: "1rem" }}>
                  No tasks or events scheduled for {formatDate(date)}.
                </p>
                {perms.add && (
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => openEventForm("task", date)}>
                    + Add Task / Event for this Date
                  </button>
                )}
              </div>
            ) : (
              items.map((item) => {
                const meta = EVENT_TYPE_META[item.type] || EVENT_TYPE_META.task;
                const isJob = item.source === "job";
                const assignment = assignmentLabel(item);
                return (
                  <div key={item.key} className={`schedule-item-card ${item.completed ? "completed" : ""}`}>
                    <div className="sched-item-header">
                      <span className={`sched-item-badge ${meta.badgeClass}`}>{meta.badge}</span>
                      <span
                        className="sched-item-status"
                        style={{ fontSize: "0.78rem", fontWeight: 600, color: item.completed ? "#10b981" : "#f59e0b" }}
                      >
                        {item.completed ? "✅ Completed" : "⌛ Pending"}
                      </span>
                    </div>
                    <div className="sched-item-title">{item.title}</div>
                    <div className="sched-item-meta">
                      <span>⏰ {formatTime12Hr(item.time) || "All day"}</span>
                      {item.location && <span>📍 {item.location}</span>}
                      {item.reminder && item.reminder !== "none" && <span>🔔 Reminder Active</span>}
                      {assignment && <span>👤 {assignment}</span>}
                    </div>
                    {item.description && <div className="sched-item-desc">{item.description}</div>}
                    <div className="sched-item-actions-bar">
                      {isJob ? (
                        <>
                          <button type="button" className="btn-action-sm btn-action-mark" onClick={() => onViewJob(item.id)}>
                            👁️ View Order
                          </button>
                          {perms.update && (
                            <button type="button" className="btn-action-sm btn-action-edit" onClick={() => onEditJob(item.id)}>
                              ✏️ Edit Job Order
                            </button>
                          )}
                        </>
                      ) : (
                        <>
                          {perms.update && (
                            <button type="button" className="btn-action-sm btn-action-mark" onClick={() => toggleEvent(item)}>
                              {item.completed ? "↩️ Mark Pending" : "✅ Mark Done"}
                            </button>
                          )}
                          {perms.update && item.can_edit && (
                            <button type="button" className="btn-action-sm btn-action-edit" onClick={() => openEventForm(item.type, item.date, item)}>
                              ✏️ Edit
                            </button>
                          )}
                          {perms.delete && item.can_edit && (
                            <button type="button" className="btn-action-sm btn-action-delete" onClick={() => deleteEvent(item)}>
                              🗑️ Delete
                            </button>
                          )}
                        </>
                      )}
                      <button type="button" className="btn-action-sm btn-action-whatsapp" onClick={() => sendWhatsApp(item)}>
                        💬 WhatsApp
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
